import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, FileText, IndianRupee, Factory, Users, TrendingUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import SEOHead from '../../components/seo/SEOHead';
import Breadcrumbs from '../../components/seo/Breadcrumbs';
import InternalLinks from '../../components/seo/InternalLinks';
import GoogleReviewsStrip from '../../components/GoogleReviewsStrip';

const HowToStartWaterBusiness = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', projectType: 'Mineral Water Business Inquiry' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submit-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed');
      const result = await response.json();
      toast.success(result.message);
      setFormData({ name: '', phone: '', projectType: 'Mineral Water Business Inquiry' });
    } catch (error) {
      toast.error('Unable to submit. Call +91-9351292512');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        title="How to Start a Mineral Water Business in India | Complete 2025 Guide | Alden Technologies"
        description="Step-by-step guide to starting a mineral water business in India: investment, machinery, BIS & FSSAI licensing, plant setup, profit margins and marketing strategy."
        path="/how-to-start-mineral-water-business"
      />
      <Breadcrumbs items={[{ label: 'How to Start a Water Business' }]} />
      <section className="relative h-[60vh] flex items-center justify-center bg-gradient-to-br from-indigo-900 via-blue-700 to-cyan-600">
        <div className="container mx-auto px-4 z-10 relative text-center">
          <div className="inline-block bg-cyan-500/90 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
            📋 Complete Business Guide
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            How to Start Mineral Water Business in India
          </h1>
          <p className="text-xl md:text-2xl text-cyan-100 mb-8 max-w-3xl mx-auto">
            Complete Setup Guide | Investment Details | Licensing & ROI Analysis
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-cyan-50 px-8 py-7 text-lg font-semibold" onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}>
              Get Free Business Plan<ArrowRight className="ml-2" />
            </Button>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-7 text-lg font-semibold">
                <Phone className="mr-2" />Expert Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Investment Required for <span className="text-blue-600">Different Plant Capacities</span>
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { id: '24bpm', capacity: '24 BPM', investment: '₹15-22 L', production: '1440/hr', suitable: 'Startup' },
              { id: '30bpm', capacity: '30 BPM', investment: '₹22-30 L', production: '1800/hr', suitable: 'Small Business' },
              { id: '40bpm', capacity: '40 BPM', investment: '₹30-40 L', production: '2400/hr', suitable: 'Medium Scale' },
              { id: '60bpm', capacity: '60 BPM', investment: '₹45-60 L', production: '3600/hr', suitable: 'Commercial' }
            ].map((item) => (
              <Card key={item.id} className="border-2 border-blue-200 hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <h3 className="text-2xl font-bold text-blue-600 mb-2">{item.capacity}</h3>
                  <p className="text-3xl font-bold text-gray-900 mb-2">{item.investment}</p>
                  <p className="text-sm text-gray-600 mb-2">{item.production}</p>
                  <p className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{item.suitable}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Complete <span className="text-blue-600">Setup Requirements</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {[
              {
                id: 'licenses',
                title: 'Required Licenses & Approvals',
                icon: FileText,
                color: 'blue',
                items: [
                  'BIS License (IS 14543 for Packaged Drinking Water)',
                  'FSSAI Registration & License',
                  'Pollution Control Board (PCB) NOC',
                  'Factory License from State Government',
                  'GST Registration',
                  'Trade License from Municipal Corporation',
                  'ISI Mark Certification'
                ]
              },
              {
                id: 'infrastructure',
                title: 'Infrastructure Requirements',
                icon: Factory,
                color: 'emerald',
                items: [
                  'Factory Space: 1500-4000 sq.ft (based on capacity)',
                  'Three Phase Electricity Connection',
                  'Adequate Water Source (Borewell/Municipal)',
                  'Proper Drainage System',
                  'Storage Area for Raw Materials & Finished Products',
                  'Office & Quality Control Lab Space'
                ]
              },
              {
                id: 'manpower',
                title: 'Manpower Requirements',
                icon: Users,
                color: 'orange',
                items: [
                  '1 Plant Manager/Supervisor',
                  '4-10 Production Workers (based on capacity)',
                  '1-2 Quality Control Staff',
                  '2-3 Sales & Distribution Personnel',
                  '1 Accountant',
                  'Technical operator training provided by us'
                ]
              },
              {
                id: 'revenue',
                title: 'Revenue & ROI Analysis',
                icon: TrendingUp,
                color: 'green',
                items: [
                  '24 BPM: ₹8-12 Lakhs monthly revenue potential',
                  '40 BPM: ₹15-20 Lakhs monthly revenue potential',
                  '60 BPM: ₹25-35 Lakhs monthly revenue potential',
                  'Typical ROI: 18-24 months',
                  'Profit Margin: 25-35% after stabilization',
                  'Break-even: 12-18 months depending on scale'
                ]
              }
            ].map((section) => (
              <Card key={section.id} className={`border-2 border-${section.color}-200`}>
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-12 h-12 bg-${section.color}-100 rounded-full flex items-center justify-center`}>
                      <section.icon className={`w-6 h-6 text-${section.color}-600`} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                  </div>
                  <ul className="space-y-3">
                    {section.items.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Why Choose <span className="text-blue-600">Alden Technologies</span> as Your Partner?
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { id: 'turnkey', title: 'Turnkey Solutions', desc: 'Complete plant setup from machinery to licensing support', icon: Factory },
              { id: 'bis', title: 'BIS Compliance', desc: 'All equipment certified to IS 14543 standards', icon: CheckCircle },
              { id: 'business', title: 'Business Support', desc: 'Marketing guidance, branding & distribution strategy help', icon: TrendingUp },
              { id: 'training', title: 'Training Included', desc: 'Comprehensive staff training for operations & maintenance', icon: Users },
              { id: 'setup', title: 'Fast Setup', desc: 'Plant operational within 30-60 days from order', icon: ArrowRight },
              { id: 'support', title: 'Lifetime Support', desc: '24/7 technical support & readily available spare parts', icon: Phone }
            ].map((item) => (
              <Card key={item.id} className="border-2 border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="quote-form" className="py-16 bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-8">
            <h2 className="text-4xl font-bold text-white mb-4">Get Free Detailed Business Plan & Project Report</h2>
            <p className="text-xl text-blue-100">Expert guidance to start your mineral water business successfully</p>
          </div>
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto bg-white rounded-lg p-8 shadow-2xl">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <Input type="text" placeholder="Your Name *" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="py-6" disabled={isSubmitting} />
              <Input type="tel" placeholder="Phone Number *" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="py-6" disabled={isSubmitting} />
            </div>
            <Button type="submit" size="lg" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-7 text-xl font-semibold" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Get Free Business Plan & Consultation'}
              {!isSubmitting && <ArrowRight className="ml-2" />}
            </Button>
          </form>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-gray-900 to-blue-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Start Your Mineral Water Business?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">Schedule a free consultation with our business experts today</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact"><Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-7 text-lg font-semibold">Schedule Consultation<ArrowRight className="ml-2" /></Button></Link>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-7 text-lg font-semibold" as="a" href="tel:+919351292512"><Phone className="mr-2" />Call: +91-9351292512</Button>
          </div>
        </div>
      </section>
      <GoogleReviewsStrip />
      <InternalLinks currentPath="/how-to-start-mineral-water-business" />
    </div>
  );
};

export default HowToStartWaterBusiness;
